import { useMemo } from 'react';

// mui
import { Box } from '@mui/material';

const BOUNCE_DURATION = 1.2; // seconds per bounce cycle
const BOUNCE_STAGGER = 0.15; // delay between neighbouring dots (makes the wave)

const dotLoadingKeyframes = {
    // Dots rise and fall in a wave; each dot rests during the last 40% of the cycle.
    '@keyframes dotLoadingBounce': {
        '0%, 60%, 100%': { transform: 'translateY(0)' },
        '30%': { transform: 'translateY(var(--dot-rise))' },
    },
    // Dots grow and shrink (and fade) in turn.
    '@keyframes dotLoadingPulse': {
        '0%, 80%, 100%': { transform: 'scale(0.4)', opacity: 0.35 },
        '40%': { transform: 'scale(1)', opacity: 1 },
    },
};

// Waiting indicator made of dots (used instead of spinners). `size` is the dot diameter in px.
//   variant='bounce' (default): 3 dots rising and falling in a wave. Use for processing (e.g. a post being moderated).
//   variant='pulse': 5 dots pulsing in size, each with its own speed. Use for loading (data being fetched).
// `count` overrides the default dot count of the variant.
// `sync` (default true): the start is aligned to a shared clock, so every indicator on screen
// moves in sync no matter when it mounted. With sync=false each instance starts at a random point
// (and, for 'pulse', each dot gets a random speed) so neighbouring indicators never move together.
function DotLoading({ variant = 'bounce', size = 8, count, color = 'primary.main', sync = true, sx }) {
    const bounce = variant === 'bounce';
    const dotCount = count ?? (bounce ? 3 : 5);

    const dots = useMemo(() => {
        const phase = sync ? (Date.now() / 1000) % BOUNCE_DURATION : Math.random() * BOUNCE_DURATION;
        return Array.from({ length: dotCount }, (_, i) => {
            if (bounce) return { duration: BOUNCE_DURATION, delay: -phase + i * BOUNCE_STAGGER };
            return sync
                ? { duration: BOUNCE_DURATION, delay: -phase + i * BOUNCE_STAGGER }
                : { duration: 0.9 + Math.random() * 0.6, delay: -(phase + i * (0.1 + Math.random() * 0.15)) };
        });
    }, [bounce, dotCount, sync]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: `${Math.max(2, Math.round(size / 2))}px`,
                '--dot-rise': `${-Math.round(size * 1.2)}px`,
                ...dotLoadingKeyframes,
                ...sx,
            }}>
            {dots.map((dot, i) => (
                <Box
                    key={i}
                    sx={{
                        width: size,
                        height: size,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: color,
                        animation: `${bounce ? 'dotLoadingBounce' : 'dotLoadingPulse'} ${dot.duration}s ease-in-out infinite`,
                        animationDelay: `${dot.delay}s`,
                    }}
                />
            ))}
        </Box>
    );
}

export { DotLoading };
